import React from 'react';
import styled from 'styled-components';
import { usePhotoBase64 } from '../hooks/usePhotoBase64';

// Page A4 avec 8 cartes (4 per colonne, 2 colonnes)
const PageA4 = styled.div`
  width: ${props => props.isPreview ? '190mm' : '210mm'};
  height: ${props => props.isPreview ? '270mm' : '297mm'};
  padding: ${props => props.isPreview ? '5mm' : '3mm'};
  background: white;
  font-family: Arial, sans-serif; /* Police simple et lisible pour toutes les informations */
  page-break-after: always;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(4, 1fr);
  gap: ${props => props.isPreview ? '3mm' : '2mm'};
  margin: ${props => props.isPreview ? '10px auto' : '0'};
  box-shadow: ${props => props.isPreview ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'};
  border: ${props => props.isPreview ? '1px solid #ddd' : 'none'};
  
  @media print {
    margin: 0;
    padding: 2mm;
    page-break-after: always;
    box-shadow: none;
    border: none;
    width: 210mm;
    height: 297mm;
    gap: 1.5mm;
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    print-color-adjust: exact;
    font-size: 14px;
  }
  
  @media screen {
    ${props => props.isPreview && `
      transform: scale(0.7);
      transform-origin: top center;
      margin-bottom: 20px;
    `}
  }
`;

// Container pour une carte professionnelle
const CarteContainer = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 10px;
  display: flex;
  flex-direction: row;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  position: relative;
  
  @media print {
    border: 1px solid #ddd;
    box-shadow: none;
    break-inside: avoid;
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    print-color-adjust: exact;
    font-size: 12px;
  }
`;

// Colonne de gauche pour les logos
const LogoColumn = styled.div`
  width: 30mm;
  height: 100%;
  background: #f0f0f0;
  border-right: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3mm;
  gap: 3mm;
  
  @media print {
    border-right: 1px solid #ddd;
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    print-color-adjust: exact;
  }
`;

// Logo principal (ECOTI) - vertical
const LogoEcoti = styled.div`
  width: 24mm;
  height: 50mm;
  background: ${props => props.logoUrl ? 'transparent' : '#e74c3c'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-top: 5mm;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  .placeholder {
    color: white;
    font-weight: bold;
    font-size: 10px;
    text-align: center;
    writing-mode: vertical-rl;
    text-orientation: mixed;
  }
  
  @media print {
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    print-color-adjust: exact;
  }
`;

// Logo secondaire (en haut à gauche)
const LogoSecondaire = styled.div`
  width: 20mm;
  height: 20mm;
  background: ${props => props.logoUrl ? 'transparent' : '#3498db'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 3mm;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
  
  .placeholder {
    color: white;
    font-weight: bold;
    font-size: 8px;
    text-align: center;
  }
  
  @media print {
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    print-color-adjust: exact;
  }
`;

// Colonne principale avec les informations
const MainColumn = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 3mm;
  gap: 1mm;
  justify-content: space-between;
`;

// Section établissement
const EtablissementSection = styled.div`
  text-align: center;
  padding: 2mm;
  border-bottom: none;
  margin-bottom: 2mm;
  
  h2 {
    font-family: Arial, sans-serif !important;
    font-size: 14px !important; /* 14px pour toutes les informations */
    font-weight: bold;
    color: #000000 !important;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 1px;
    
    @media print {
      font-family: Arial, sans-serif !important;
      font-size: 14px !important; /* 14px même à l'impression */
      font-weight: bold !important;
      color: #000000 !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Section type de carte
const TypeCarteSection = styled.div`
  text-align: center;
  padding: 1mm;
  border-bottom: none;
  margin-bottom: 2mm;
  
  h3 {
    font-family: Arial, sans-serif !important;
    font-size: 14px !important; /* 14px pour toutes les informations */
    font-weight: bold;
    color: #000000 !important;
    margin: 0;
    text-transform: uppercase;
    
    @media print {
      font-family: Arial, sans-serif !important;
      font-size: 14px !important; /* 14px même à l'impression */
      font-weight: bold !important;
      color: #000000 !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Section photo - 80% de la hauteur de la carte (PHOTO = ESSENTIEL)
const PhotoSection = styled.div`
  width: 100%;
  height: 80%; /* 80% de la hauteur de la carte - PHOTO ESSENTIELLE */
  border: none; /* Supprimé le contour */
  border-radius: 4px;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.2mm 0; /* Espacement minimal */
  padding: 0;
  box-sizing: border-box;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain; // 'contain' pour voir tous les contours de la photo
    background-color: #f8f9fa; // Fond pour les espaces vides
  }
  
  .no-photo {
    font-size: 14px;
    color: #7f8c8d;
    text-align: center;
    font-weight: bold;
    background: #f8f9fa;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  @media print {
    border: none !important; /* Supprimé le contour pour l'impression */
    border-radius: 4px !important;
    overflow: hidden !important;
    background: #f8f9fa !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 2mm 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
    height: 80% !important; /* 80% de la hauteur de la carte - PHOTO ESSENTIELLE */
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
    print-color-adjust: exact !important;
    
    img {
      width: 100% !important;
      height: 100% !important;
      object-fit: contain !important; // 'contain' pour voir tous les contours
      background-color: #f8f9fa !important; // Fond pour les espaces vides
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .no-photo {
      font-size: 14px !important;
      color: #7f8c8d !important;
      text-align: center !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

// Section nom et prénoms
const NomSection = styled.div`
  padding: 0 !important; /* Supprimé l'espacement vertical */
  border: none;
  margin-bottom: 0 !important; /* Supprimé l'espacement vertical */
  display: flex !important;
  align-items: center;
  justify-content: center;
  min-height: 2mm !important; /* Hauteur minimale */
  background-color: transparent;
  visibility: visible !important;
  opacity: 1 !important;
  
  .nom-complet {
    font-family: Arial, sans-serif !important;
    font-size: 14px !important; /* 14px pour toutes les informations */
    font-weight: bold !important;
    color: #000000 !important;
    text-align: center !important;
    line-height: 1 !important; /* Line-height réduit */
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    
    @media print {
      font-family: Arial, sans-serif !important;
      font-size: 14px !important; /* 14px même à l'impression */
      font-weight: bold !important;
      color: #000000 !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Section matricule
const MatriculeSection = styled.div`
  display: flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5mm !important; /* Espacement minimal */
  margin-bottom: 0 !important; /* Supprimé l'espacement vertical */
  padding: 0 !important; /* Supprimé l'espacement vertical */
  min-height: 1.5mm !important; /* Hauteur minimale */
  background-color: transparent;
  border: none;
  visibility: visible !important;
  opacity: 1 !important;
  
  .value {
    font-family: Arial, sans-serif !important;
    font-size: 14px !important; /* 14px pour toutes les informations */
    font-weight: bold !important;
    color: #000000 !important;
    text-align: center !important;
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    
    @media print {
      font-family: Arial, sans-serif !important;
      font-size: 14px !important; /* 14px même à l'impression */
      font-weight: bold !important;
      color: #000000 !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Section fonction
const FonctionSection = styled.div`
  padding: 0 !important; /* Supprimé l'espacement vertical */
  border: none;
  display: flex !important;
  align-items: center;
  justify-content: center;
  min-height: 1.5mm !important; /* Hauteur minimale */
  background-color: transparent;
  visibility: visible !important;
  opacity: 1 !important;
  
  .fonction-value {
    font-family: Arial, sans-serif !important;
    font-size: 14px !important; /* 14px pour toutes les informations */
    font-weight: bold !important;
    color: #000000 !important;
    text-align: center !important;
    line-height: 1 !important; /* Line-height réduit */
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    
    @media print {
      font-family: Arial, sans-serif !important;
      font-size: 14px !important; /* 14px même à l'impression */
      font-weight: bold !important;
      color: #000000 !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Container pour les informations agent
const AgentInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1mm;
  flex: 1;
  justify-content: center;
`;

// Label d'aperçu
const PreviewLabel = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 10px;
  margin-bottom: 20px;
  border-radius: 5px;
  text-align: center;
  font-size: 14px;
  color: #495057;
  font-weight: 500;
  
  small {
    display: block;
    margin-top: 5px;
    color: #6c757d;
    font-weight: normal;
  }
`;

// Composant pour afficher la photo - COPIE EXACTE de CartePointage4ParPage
const PhotoDisplay = ({ photoPath, alt }) => {
  const { photoBase64, loading } = usePhotoBase64(photoPath);
  
  if (loading) {
    return (
      <div className="no-photo">
        Chargement...
      </div>
    );
  }
  
  if (!photoBase64) {
    return (
      <div className="no-photo">
        PHOTO
      </div>
    );
  }
  
  return (
    <img 
      src={photoBase64} 
      alt={alt} 
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain', // 'contain' pour voir tous les contours
        borderRadius: '4px',
        backgroundColor: '#f8f9fa' // Fond pour les espaces vides
      }}
    />
  );
};

// Composant principal
const CarteProfessionnelle = ({ 
  cartes, 
  isPreview = false, 
  logoEcotiUrl = null,
  logoSecondaireUrl = null 
}) => {
  if (!cartes || cartes.length === 0) return null;
  
  // Log détaillé pour chaque carte
  console.log('CarteProfessionnelle - Cartes reçues:', cartes);
  cartes.forEach((carte, index) => {
    console.log(`Carte ${index}:`, {
      agent: carte.agent,
      hasPhoto: !!carte.agent?.photoPath,
      photoPath: carte.agent?.photoPath,
      fonction: carte.agent?.fonction,
      nom: carte.agent?.nom,
      prenoms: carte.agent?.prenoms,
      matricule: carte.agent?.matricule
    });
  });

  // Diviser les cartes en groupes de 8 pour chaque page A4
  const pages = [];
  for (let i = 0; i < cartes.length; i += 8) {
    pages.push(cartes.slice(i, i + 8));
  }

  return (
    <>
      {isPreview && (
        <PreviewLabel>
          📄 APERÇU D'IMPRESSION - Format A4 (8 cartes professionnelles par page)
          <br />
          <small>Cet aperçu montre exactement ce qui sera imprimé</small>
        </PreviewLabel>
      )}
      
      {pages.map((pageCartes, pageIndex) => (
        <PageA4 key={pageIndex} isPreview={isPreview}>
          {pageCartes.map((carte, carteIndex) => {
            const { agent } = carte;

            return (
              <CarteContainer key={carteIndex}>
                {/* Colonne des logos */}
                <LogoColumn>
                  {/* Logo secondaire en haut */}
                  <LogoSecondaire logoUrl={logoSecondaireUrl}>
                    {logoSecondaireUrl ? (
                      <img src={logoSecondaireUrl} alt="Logo Secondaire" />
                    ) : (
                      <div className="placeholder">Logo</div>
                    )}
                  </LogoSecondaire>
                  
                  {/* Logo ECOTI vertical */}
                  <LogoEcoti logoUrl={logoEcotiUrl}>
                    {logoEcotiUrl ? (
                      <img src={logoEcotiUrl} alt="Logo ECOTI" />
                    ) : (
                      <div className="placeholder">ECOTI</div>
                    )}
                  </LogoEcoti>
                </LogoColumn>

                {/* Colonne principale */}
                <MainColumn>
                  {/* Établissement */}
                  <EtablissementSection>
                    <h2>ETABLISSEMENTS COULIBALY</h2>
                  </EtablissementSection>

                  {/* Type de carte */}
                  <TypeCarteSection>
                    <h3>CARTE PROFESSIONNELLE</h3>
                  </TypeCarteSection>

                  {/* Photo */}
                  <PhotoSection>
                    <PhotoDisplay 
                      photoPath={agent.photoPath} 
                      alt={`${agent.nom} ${agent.prenoms}`} 
                    />
                  </PhotoSection>

                  {/* Informations agent */}
                  <AgentInfoContainer>
                    {/* Nom et prénoms */}
                    <NomSection>
                      <div className="nom-complet">
                        {agent.nom} {agent.prenoms}
                      </div>
                    </NomSection>

                    {/* Matricule */}
                    <MatriculeSection>
                      <span className="value">{agent.matricule || 'N/A'}</span>
                    </MatriculeSection>

                    {/* Fonction */}
                    <FonctionSection>
                      <div className="fonction-value">
                        {agent.fonction || 'N/A'}
                      </div>
                    </FonctionSection>
                  </AgentInfoContainer>
                </MainColumn>
              </CarteContainer>
            );
          })}
          
          {/* Remplir avec des cases vides si moins de 8 cartes */}
          {Array.from({ length: 8 - pageCartes.length }).map((_, index) => (
            <div key={`empty-${index}`}></div>
          ))}
        </PageA4>
      ))}
    </>
  );
};

export default CarteProfessionnelle;
